/**
 * DispatchAuditControls — reusable header controls & prompts for the 5
 * modules in the Dispatch bundle (v109.8):
 *   Purchase Product, Product Issuance, Vendor Management, Inventory,
 *   Dispatch & Logistics.
 *
 * Exports:
 *   • <DispatchModuleAccessButton moduleKey label />
 *       Master-Admin-only Users icon button that opens ModuleAccessDialog.
 *   • <ReasonPromptDialog … />
 *       Small confirm dialog that captures a mandatory `reason` before an
 *       edit or delete request. Returns the reason via onConfirm(reason).
 *   • useReasonPrompt()  — hook that returns {ask, dialog}. Call ask() to
 *       queue a prompt and await the reason. Renders dialog inline.
 */
import { useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

/**
 * v109.9.40 — Module Access button removed per user directive. Kept the export
 * as a no-op so any lingering `<DispatchModuleAccessButton />` call sites still
 * mount without throwing. Once every caller is confirmed clean the export can
 * be dropped entirely.
 */
export function DispatchModuleAccessButton() {
  return null;
}

export function ReasonPromptDialog({
  open, onOpenChange, title, description, action = "update",
  minLength = 3, onConfirm, confirmLabel,
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const clean = reason.trim();
  const disabled = clean.length < minLength || saving;
  const label = confirmLabel || (action === "delete" ? "Confirm Delete" : "Save Changes");
  const tone = action === "delete" ? "bg-rose-600 hover:bg-rose-700" : "bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)]";

  const submit = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      await onConfirm(clean);
      setReason("");
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); if (!v) setReason(""); }}>
      <DialogContent className="max-w-md" data-testid="reason-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="heading text-[15px]">{title || (action === "delete" ? "Reason for deletion" : "Reason for change")}</DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-slate-600 mb-2">
          {description || (action === "delete"
            ? "This record will be soft-deleted and permanently purged after 30 days. Please state the reason (recorded in the audit log)."
            : "Please state a brief reason for this change (recorded in the audit log).")}
        </p>
        <Textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="e.g. Duplicate entry / customer request / stock correction …"
          className="w-full text-[13px]"
          data-testid="reason-prompt-input"
        />
        <div className="text-[10.5px] text-slate-400 flex items-center justify-between">
          <span>{clean.length < minLength ? `Enter at least ${minLength} characters` : "Looks good"}</span>
          <span>{reason.length}/500</span>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={disabled} className={`${tone} text-white`} data-testid="reason-prompt-confirm">
            {saving ? "Saving…" : label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * useReasonPrompt — imperative hook that yields:
 *   {ask, dialog}
 *     ask({title, description, action, confirmLabel}) → Promise<string|null>
 *     dialog: JSX to render inline in the page (single instance, reused).
 */
export function useReasonPrompt() {
  const [state, setState] = useState({ open: false, opts: {}, resolver: null });

  const ask = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, opts, resolver: resolve });
    });
  }, []);

  const handleConfirm = async (reason) => {
    state.resolver?.(reason);
    setState({ open: false, opts: {}, resolver: null });
  };
  const handleOpenChange = (v) => {
    if (!v) {
      state.resolver?.(null);
      setState({ open: false, opts: {}, resolver: null });
    } else {
      setState((prev) => ({ ...prev, open: true }));
    }
  };

  const dialog = (
    <ReasonPromptDialog
      open={state.open}
      onOpenChange={handleOpenChange}
      title={state.opts.title}
      description={state.opts.description}
      action={state.opts.action}
      confirmLabel={state.opts.confirmLabel}
      onConfirm={handleConfirm}
    />
  );

  return { ask, dialog };
}
